import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import styled, { css, withTheme } from 'styled-components';
import constants from 'constants/index';
import { getColor } from 'utils';

const light = constants.THEME.LIGHT;
const padding = 0.5;

const PopoverContainer = styled('div')`
  position: relative;
  display: flex;
  justify-content: center;
  user-select: none;
  cursor: pointer;
`;

const ChildContainer = styled('div')`
  display: flex;
  user-select: none;
  cursor: pointer;
`;

const popoverPositions = props => {
  const { top, right, bottom, left } = props;
  const positions = [];

  const addPosition = (name, value, opposite) => {
    if (value && !positions.includes(opposite)) {
      const styleString = css`${name}: ${(value ?? 0) / 16}rem;`;
      positions.push(styleString);
    }
  };

  addPosition('top', top, 'bottom');
  addPosition('right', right, 'left');
  addPosition('bottom', bottom, 'top');
  addPosition('left', left, 'right');

  return positions.flat();
};

const InnerContent = styled('div')`
  position: absolute;
  ${props => popoverPositions(props)}
  z-index: 3;
  padding: ${props => (props.padding ? props.padding / 16 : padding) * 2}rem;
  border-radius: 0.25rem;
  background-color: ${props => getColor(props, props.$selectedTheme === light ? 'primary' : 'darkGrey')};
  color: ${props => getColor(props, 'onPrimary')};
  ${props => props.$minWidth && css`min-width: ${props.$minWidth / 16}rem`};
  width: max-content;
  height: fit-content;
  filter: drop-shadow(0 0.1rem 0.25rem ${props => (
    getColor(props, props.$selectedTheme === light ? 'darkGrey' : 'black')
  )});
  user-select: none;
`;

const arrowPosition = position => {
  const offset = 0.6 * -1;

  switch(position) {
    case 'right':
      return css`
        left: ${offset}rem;
        top: calc(50% - ${padding}rem)`;

    case 'top':
      return css`
        bottom: ${offset}rem;
        right: calc(50% - ${padding}rem)`;

    case 'left':
      return css`
        right: ${offset}rem;
        top: calc(50% - ${padding}rem)`;

    default:
      return css`
        top: ${offset}rem;
        right: calc(50% - ${padding}rem)`;
  }
};

const Arrow = styled('div')`
  position: absolute;
  ${props => arrowPosition(props.position)};
  width: 1.25rem;
  height: 1.25rem;
  transform: rotate(45deg);
  background-color: ${props => getColor(props, props.$selectedTheme === light ? 'primary' : 'darkGrey')};
`;

const FovPopover = withTheme(props => {
  const {
    theme,
    position,
    content,
    children,
    top,
    right,
    bottom,
    left,
    noArrow,
    callback,
    keepOpened = true,
    disabled,
    padding,
    $minWidth,
    isVisible,
    ...rest
  } = props;

  const [ visible, setVisible ] = useState(false);
  const userDataStore = useSelector(state => state.userData);
  const selectedTheme = userDataStore.userPreferences.theme;
  const bounds = useRef();

  useEffect(() => {
      if (typeof isVisible === 'boolean' && visible !== isVisible) setVisible(isVisible);
  }, [ isVisible, setVisible ]);

  const handleOffClickClose = useCallback(e => {
    if (e.target !== bounds.current) {
      setVisible(false);
      callback ? callback() : _.noop();
    }
  }, [ setVisible, callback, bounds.current ]);

  useEffect(() => {
    if (visible && bounds.current) {
      window.addEventListener('click', handleOffClickClose);
    }

    return () => window.removeEventListener('click', handleOffClickClose);
  }, [ visible, bounds.current ]);

  return (
    <PopoverContainer
      ref={bounds}
      theme={theme}
      $selectedTheme={selectedTheme}
      {...rest}
      >
      <ChildContainer
        onClick={e => {
          if (!disabled) {
            e.stopPropagation();
            setVisible(!visible);
            callback ? callback() : _.noop();
          }
        }}
      >
        {children}
        </ChildContainer>

        {visible && (
          <InnerContent
            theme={theme}
            $selectedTheme={selectedTheme}
            $minWidth={$minWidth}
            top={top}
            right={right}
            bottom={bottom}
            left={left}
            padding={padding}
            onClick={e => {
              if (!disabled) {
                e.stopPropagation();
                if (keepOpened) {
                  setVisible(!visible);
                  callback ? callback() : _.noop();
                }
              }
            }}
        >
          {content}
          {!noArrow && (
            <Arrow
              theme={theme}
              $selectedTheme={selectedTheme}
              position={position}
            />
          )}
        </InnerContent>
      )}
    </PopoverContainer>
  );
});

export { FovPopover };
